import { type LazyExoticComponent, type FC } from 'react'
import { lazyWithReload, lazyWithReload as lazy } from '../shell/lazyWithReload'
import type { AppRole } from '../auth/usePermissions'

// ── Module Definition ────────────────────────────────────────────────────────

export interface ModuleDefinition {
  id: string
  path: string
  label: string
  icon: string
  /**
   * @deprecated Authorisation now resolves through `ops.RoleModuleAccess`
   * (the DB) at runtime — see `lib/permissions.js` and
   * `auth/useEffectivePermissions.ts`. Remaining occurrences are no-ops at
   * runtime and exist only to satisfy the legacy `RoleDetailDrawer` fallback
   * shape during the migration window. New entries should omit `roles`.
   */
  roles?: AppRole[]
  component: LazyExoticComponent<FC>
  children?: Omit<ModuleDefinition, 'children'>[]
  /** Skip auto-route generation in router.tsx. Use when the path is mounted
   *  manually elsewhere (e.g. as part of a nested route group with its own
   *  layout). The module still appears in admin / permission UIs. */
  noRoute?: boolean
  /** No page exists behind this entry (permission right / widget only). The command
   *  palette must not offer it. Not the same as noRoute: other noRoute entries are
   *  pages mounted by hand in router.tsx. */
  noPage?: boolean
}

// ── Lazy page imports ────────────────────────────────────────────────────────
// Each module gets its own chunk — loaded only when navigated to

const DashboardPage = lazy(() => import('../pages/DashboardPage'))
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'))
const AccessDeniedPage = lazy(() => import('../pages/AccessDeniedPage'))

// ── Module imports ────────────────────────────────────────────────────────────
const FleetOps = lazy(() => import('./fleetops/FleetOps'))
const RoleAdmin = lazy(() => import('./admin/RoleAdmin'))
const FirmaPolicyPage = lazy(() => import('./admin/FirmaPolicyPage'))
const SchuljahrPolicyPage = lazy(() => import('./admin/SchuljahrPolicyPage'))
const EventLogPage = lazy(() => import('./admin/EventLogPage'))
const SettingsPage = lazy(() => import('./settings/Settings'))
// Placeholder for registry entries that exist only so /it/role-admin lists
// them: the Daten-Bot (M25, lives in the floating BotWidget) and modules the
// backend enforces on manually-mounted routes (HR Personenakte tabs, DATEV).
// noRoute means router.tsx never renders it; lazy() still needs a
// LazyExoticComponent, so this resolves inline.
const NoRoutePlaceholder = lazy(() => Promise.resolve({ default: () => null }))

// IT
const ITDashboard = lazy(() => import('./it/ITDashboard'))
const ITCompliance = lazy(() => import('./it/ITCompliance'))
const TechStackPage = lazyWithReload(() => import('./compliance/techstack/TechStackPage'))

// Schulen (legacy — kept for backwards compatibility)
const SchulenDashboard = lazy(() => import('./schulen/SchulenDashboard'))
const SchulenHierarchie = lazy(() => import('./schulen/hierarchie/HierarchieView'))
const KartenPage = lazyWithReload(() => import('./schulen/karten/KartenPage'))
const LieferstellenPage = lazyWithReload(() => import('./schulen/lieferstellen/LieferstellenPage'))
const KuechenPage = lazyWithReload(() => import('./schulen/KuechenPage'))
const SperrtagKontrollePage = lazyWithReload(() => import('./schulen/SperrtagKontrollePage'))

// Betrieb (new operations module)
const CommandCenter = lazy(() => import('./betrieb/CommandCenter'))
const ProjektePage = lazy(() => import('./betrieb/ProjektePage'))
const StandortePage = lazy(() => import('./betrieb/StandortePage'))
const OrganigrammPage = lazy(() => import('./betrieb/OrganigrammPage'))
const BetriebsplanungPage = lazy(() => import('./betrieb/BetriebsplanungPage'))
const TourenPage = lazy(() => import('./betrieb/touren/TourenPage'))

// Support
const SupportDashboard = lazy(() => import('./support/SupportDashboard'))
const ButMasterPage = lazy(() => import('./support/butMaster/ButMasterPage'))
// M27 F2/F3 (T0 skeleton; module ids = ops.RoleModuleAccess rows of migration 378)
const TurboSupportBuero = lazy(() => import('./support/turbo/TurboSupportPage'))
const SupportAuswertung = lazy(() => import('./support/turbo/AuswertungPage'))
const ItHelpdeskBuero = lazy(() => import('./it/helpdesk/ItHelpdeskPage'))
// M27 T15: IT opens and closes the Schulportal-Module per school (module id = ops.RoleModuleAccess row of migration 382)
const SchulportalModule = lazyWithReload(() => import('./it/schulmodule/SchulModulePage'))
const MegaTabelleV2Datenpull = lazy(() => import('./support/MegaTabelleV2Datenpull'))
const SchuelerPCA = lazy(() => import('./support/SchuelerPCA'))
const MegaTabellePca = lazy(() => import('./support/MegaTabellePcaPage'))

// Operations
const SpuelerPage = lazy(() => import('./spueler/SpuelerPage'))
const InventarPage = lazy(() => import('./inventar/InventarPage'))

// HR
const HRDashboard = lazy(() => import('./hr/HRDashboard'))
const HrPersonalUebersicht = lazy(() => import('./hr/dashboard/HrDashboardPage'))
const HrVertragszeitAbgleich = lazy(() => import('./hr/vertragszeit/VertragszeitAbgleichPage'))

// Zeiterfassung — cockpit over Personal + Spüler (eigen) + externe
// Spüler-Firmen (CleanPrompt, A.H.). IT.Admin only until the owner grants
// roles in /it/role-admin (see functions/intranet/src/functions/zeitCockpit.js).
const ZeitCockpitPage = lazy(() => import('./zeiterfassung/ZeitCockpitPage'))

// HR — Onboarding (Probearbeiter ingress + multi-step wizard)
const OnboardingQueuePage      = lazy(() => import('./hr/onboarding/OnboardingQueuePage').then(m => ({ default: m.OnboardingQueuePage })))
const ProbearbeiterFormPage    = lazy(() => import('./hr/onboarding/ProbearbeiterFormPage').then(m => ({ default: m.ProbearbeiterFormPage })))
const ProbearbeiterReviewPage  = lazy(() => import('./hr/onboarding/ProbearbeiterReviewPage').then(m => ({ default: m.ProbearbeiterReviewPage })))
const OnboardingWizard         = lazy(() => import('./hr/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })))

// HR — Contracts (M11)
const ContractPage             = lazy(() => import('./hr/contracts/ContractPage').then(m => ({ default: m.ContractPage })))
const ContractTemplatesAdmin   = lazy(() => import('./hr/contracts/TemplatesAdminPage').then(m => ({ default: m.TemplatesAdminPage })))

// Verwaltung — Lieferanten (supplier registry, source of truth for supplier metadata)
const LieferantenConsole = lazy(() => import('./verwaltung/lieferanten/LieferantenConsole'))

// Verwaltung — Kostenarten (Phase 3 Kostenart restructure — dim.Kostenart admin)
const KostenartenPage = lazyWithReload(() => import('./verwaltung/kostenarten/KostenartenPage'))

// Verwaltung — Mitarbeiterportal (M26 admin surface, Task 14). Backend (Task
// 13, parallel PR) lives under /api/staff-portal/admin/*. Split into four
// sections (Einstellungen/Schulungen/Dokumente/Mitteilungen), each its own
// registry module — module ids MUST match the backend's
// requirePermission(actor, MODULE_ID, ...) calls exactly.
// 'mitarbeiterportal-einstellungen' has no seeded ops.RoleModuleAccess row on
// purpose — IT.Admin only, hidden for everyone else via canRead.
const MitarbeiterportalEinstellungenPage = lazyWithReload(() => import('./verwaltung/mitarbeiterportal/pages/EinstellungenPage'))
const MitarbeiterportalSchulungenPage    = lazyWithReload(() => import('./verwaltung/mitarbeiterportal/pages/SchulungenPage'))
const MitarbeiterportalDashboardPage     = lazyWithReload(() => import('./verwaltung/mitarbeiterportal/pages/DashboardPage'))
const MitarbeiterportalDokumentePage     = lazyWithReload(() => import('./verwaltung/mitarbeiterportal/pages/DokumentePage'))
const MitarbeiterportalMitteilungenPage  = lazyWithReload(() => import('./verwaltung/mitarbeiterportal/pages/MitteilungenPage'))

// Verwaltung — HACCP (cold-chain temperature monitoring; P1 ingest backend built
// in parallel on feat/haccp-p1). Leitstand = 'haccp'; each detail page has its
// own module (haccp-geraet/-berichte/-einstellungen/-onboarding, seeded from
// 'haccp' by mig 335). The HACCP API itself still checks 'haccp'.
const HaccpLeitstandPage = lazyWithReload(() => import('./haccp/HaccpLeitstandPage'))
const HaccpGeraetPage    = lazyWithReload(() => import('./haccp/HaccpGeraetPage'))
const HaccpBerichtePage  = lazyWithReload(() => import('./haccp/HaccpBerichtePage'))
const HaccpEinstellungenPage = lazyWithReload(() => import('./haccp/HaccpEinstellungenPage'))
const HaccpOnboardingPage = lazyWithReload(() => import('./haccp/HaccpOnboardingPage'))

// M30 QM — Leitung. 'qm' = the offline field app (own entry /qm/, mounted by hand in router.tsx);
// 'qm-leitung' = the Leitung pages. Übersicht, Kalender, Aufgaben, Nachrichten and the three setup pages (Checkliste, Positionen & Küchen, Intervall)
// are mounted by hand in router.tsx and share the module 'qm-leitung'; /qm-leitung itself lands on the Übersicht (query kept).

// Gastromatic — Importlisten für die Lohnabrechnung aus FileMaker (nur lesend). Backend:
// functions/intranet/src/functions/gastromatic.js (MODULE_ID 'gastromatic'). IT.Admin only until the
// owner grants roles in /it/role-admin (no seeded ops.RoleModuleAccess row).
const GastromaticPage = lazyWithReload(() => import('./gastromatic/GastromaticPage'))

// PCA — top-level portal analytics (Anmeldezahlen = account registrations)
const PcaAnmeldezahlenPage = lazyWithReload(() => import('./pca/PcaAnmeldezahlenPage'))
const AnmeldekontrollePage = lazyWithReload(() => import('./pca/AnmeldekontrollePage'))

// Verwaltung — Kostenstellen + Lieferanten advanced views.
// Routes are mounted manually in router.tsx (nested route group with the
// KostenstellenPage layout-rail). They're listed here purely for admin
// surfaces (RoleAdmin, RoleGuard, sidebar discovery) and carry noRoute:true
// so router.tsx doesn't double-register them.
const KostenstellenLayout      = lazy(() => import('./verwaltung/kostenstellen/KostenstellenPage'))
const KostenstellenUebersicht  = lazy(() => import('./verwaltung/kostenstellen/pages/UebersichtPage'))
const KostenstellenImports     = lazy(() => import('./verwaltung/kostenstellen/pages/ImportsPage'))
const KostenstellenStammdaten  = lazy(() => import('./verwaltung/kostenstellen/pages/StammdatenPage'))
const KostenstellenRefood      = lazy(() => import('./verwaltung/kostenstellen/pages/RefoodPage'))
const KostenstellenAuswertung  = lazy(() => import('./verwaltung/kostenstellen/pages/AuswertungPage'))
const KostenstellenLieferanten = lazy(() => import('./verwaltung/kostenstellen/pages/LieferantenAuswertungPage'))
const KostenstellenPreiskatalog = lazy(() => import('./verwaltung/kostenstellen/pages/PreiskatalogPage'))
const KostenstellenBioQuote = lazy(() => import('./verwaltung/kostenstellen/pages/BioQuotePage'))
const KostenstellenPcaExplorer  = lazy(() => import('./verwaltung/kostenstellen/pages/PcaExplorerPage'))
const KostenstellenSapKasse     = lazy(() => import('./verwaltung/kostenstellen/pages/SapKassePage'))
const KasseKontrolle            = lazy(() => import('./kasse/KontrollePage'))
const LieferantenKategorisierung = lazy(() => import('./verwaltung/lieferanten/pages/KategorisierungPage'))
const LieferantenWorkbench       = lazy(() => import('./verwaltung/lieferanten/pages/MappingWorkbenchPage'))
const VerwaltungKpiSettings      = lazy(() => import('./verwaltung/einstellungen/pages/KpiSettingsPage'))

// Bestellkorrektur
const BestellkorrekturPage = lazyWithReload(() => import('./bestellkorrektur/BestellkorrekturPage'))

// M23 Speiseplan
const SpeiseplanPage = lazyWithReload(() => import('./speiseplan/SpeiseplanPage'))
const GerichtePage = lazyWithReload(() => import('./speiseplan/GerichtePage'))

// Design System Templates
const GlassTemplate1 = lazy(() => import('./design-system/GlassTemplate1'))
const GlassTemplate2 = lazy(() => import('./design-system/GlassTemplate2'))
const MotionTemplate1 = lazy(() => import('./design-system/MotionTemplate1'))
const MotionTemplate2 = lazy(() => import('./design-system/MotionTemplate2'))
const ComponentGallery = lazy(() => import('./design-system/ComponentGallery'))
const LiquidTemplate = lazy(() => import('./design-system/LiquidTemplate'))

// ── Registry ─────────────────────────────────────────────────────────────────

export const modules: ModuleDefinition[] = [
  {
    id: 'dashboard',
    path: '/',
    label: 'Dashboard',
    icon: 'grid_view',
    component: DashboardPage,
  },
  {
    id: 'inventar',
    path: '/inventar',
    label: 'Inventar',
    icon: 'inventory_2',
    component: InventarPage,
  },
  {
    id: 'spueler',
    path: '/spueler',
    label: 'Spüler',
    // `mop` reads as a thin line-drawing in the outlined font, matching
    // the rest of the nav. `cleaning_services` has solid bristles and
    // breaks the visual rhythm.
    icon: 'mop',
    component: SpuelerPage,
  },
  {
    id: 'hr',
    path: '/hr',
    label: 'Personal',
    icon: 'badge',
    component: HRDashboard,
  },
  // HR · Personalübersicht — attendance dashboard (temporary until Gastromatic).
  // Access: IT.Admin / Verwaltung.Leitung / Operations.Manager (mig 242 + API gate).
  {
    id: 'hr-dashboard',
    path: '/hr/dashboard',
    label: 'Personalübersicht',
    icon: 'event_available',
    component: HrPersonalUebersicht,
  },
  // HR · Vertragszeit-Abgleich — its OWN right, so /it/role-admin lists it (group "Hr") and the owner grants Lesen / Lesen + Schreiben per role.
  // Mounted manually in router.tsx (RoleGuard module="hr-vertragszeit-abgleich"); the API checks the same module (lib/vertragszeitAbgleich.js).
  // Migration 402 grants it to IT.Admin, Verwaltung.Leitung, Operations.Manager. Until 402 ran, auth/moduleFallback.ts lets the old right of hr-dashboard apply.
  {
    id: 'hr-vertragszeit-abgleich',
    path: '/hr/vertragszeit-abgleich',
    label: 'Vertragszeit-Abgleich',
    icon: 'compare_arrows',
    component: HrVertragszeitAbgleich,
    noRoute: true,
  },
  // Zeiterfassung — Cockpit über alle Arbeitszeiten (Personal, Spüler eigen,
  // CleanPrompt, A.H.). Access: IT.Admin only until the owner grants roles.
  {
    id: 'zeiterfassung',
    path: '/zeiterfassung',
    label: 'Zeiterfassung',
    icon: 'schedule',
    component: ZeitCockpitPage,
  },
  // Zeiterfassung · Doppelbuchungen — a tab of the cockpit (no own route), its own right so Role-Admin can show and
  // grant it (mig 360: IT.Admin only, owner 08.10.2026). Deleting removes the record in FileMaker.
  {
    id: 'zeiterfassung-doppelbuchungen',
    path: '/zeiterfassung/doppelbuchungen',
    label: 'Zeiterfassung · Doppelbuchungen',
    icon: 'content_copy',
    component: ZeitCockpitPage,
    noRoute: true,
    noPage: true,
  },
  // Gastromatic — Importlisten (Mitarbeiter aus FileMaker → verschlüsselte Excel-Vorlage).
  // Access: IT.Admin only until the owner grants roles in /it/role-admin.
  {
    id: 'gastromatic',
    path: '/gastromatic',
    label: 'Gastromatic',
    icon: 'upload_file',
    component: GastromaticPage,
  },
  // HR · Onboarding — queue page (Probearbeiter + drafts)
  {
    id: 'hr-onboarding',
    path: '/hr/onboarding',
    label: 'Onboarding',
    icon: 'person_add',
    component: OnboardingQueuePage,
  },
  // Sub-routes mounted manually in router.tsx (dynamic :id segments).
  // Listed here with noRoute:true so RoleAdmin / permission UIs still see them.
  {
    id: 'hr-onboarding-probe-new',
    path: '/hr/onboarding/probearbeiter/new',
    label: 'Probearbeiter anlegen',
    icon: 'person_pin',
    component: ProbearbeiterFormPage,
    noRoute: true,
  },
  {
    id: 'hr-onboarding-probe-review',
    path: '/hr/onboarding/probearbeiter/:id',
    label: 'Probearbeiter prüfen',
    icon: 'fact_check',
    component: ProbearbeiterReviewPage,
    noRoute: true,
  },
  {
    id: 'hr-onboarding-wizard',
    path: '/hr/onboarding/wizard/:personMainId',
    label: 'Onboarding-Wizard',
    icon: 'list_alt',
    component: OnboardingWizard,
    noRoute: true,
  },
  // HR · Verträge (M11)
  {
    id: 'hr-contracts',
    path: '/hr/personen/:mainId/vertrag/:vertragId/contract',
    label: 'Vertrag',
    icon: 'description',
    component: ContractPage,
    noRoute: true,
  },
  {
    id: 'hr-contracts-templates',
    path: '/hr/contracts/templates',
    label: 'Vertragsvorlagen',
    icon: 'description',
    component: ContractTemplatesAdmin,
  },
  // HR · Personenakte — mounted manually in router.tsx; the backend gates the
  // page on 'hr-person-detail' and each tab on its own 'hr-person-*' module
  // (functions/hrPerson.js HR_PERSON_TAB_MODULES). Listed here so they are
  // managed in /it/role-admin.
  {
    id: 'hr-person-detail',
    path: '/hr/personen/:mainId',
    label: 'Personenakte',
    icon: 'person',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-uebersicht',
    path: '/hr/personen/:mainId/uebersicht',
    label: 'Personenakte · Übersicht',
    icon: 'dashboard',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-stammdaten',
    path: '/hr/personen/:mainId/stammdaten',
    label: 'Personenakte · Stammdaten',
    icon: 'badge',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-bank-steuer',
    path: '/hr/personen/:mainId/bank-steuer',
    label: 'Personenakte · Bank & Steuer',
    icon: 'account_balance',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-beschaeftigung',
    path: '/hr/personen/:mainId/beschaeftigung',
    label: 'Personenakte · Beschäftigung',
    icon: 'work',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-vertraege',
    path: '/hr/personen/:mainId/vertraege',
    label: 'Personenakte · Verträge',
    icon: 'description',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-zeiterfassung',
    path: '/hr/personen/:mainId/zeiterfassung',
    label: 'Personenakte · Zeiterfassung',
    icon: 'schedule',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-lohn',
    path: '/hr/personen/:mainId/lohn',
    label: 'Personenakte · Lohn',
    icon: 'payments',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-urlaub',
    path: '/hr/personen/:mainId/urlaub',
    label: 'Personenakte · Urlaub',
    icon: 'beach_access',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-krank',
    path: '/hr/personen/:mainId/krank',
    label: 'Personenakte · Krankmeldungen',
    icon: 'sick',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-compliance',
    path: '/hr/personen/:mainId/compliance',
    label: 'Personenakte · Compliance',
    icon: 'verified_user',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-dokumente',
    path: '/hr/personen/:mainId/dokumente',
    label: 'Personenakte · Dokumente',
    icon: 'folder',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-notizen',
    path: '/hr/personen/:mainId/notizen',
    label: 'Personenakte · Notizen',
    icon: 'sticky_note_2',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-historie',
    path: '/hr/personen/:mainId/historie',
    label: 'Personenakte · Historie',
    icon: 'history',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-person-offboarding',
    path: '/hr/personen/:mainId/offboarding',
    label: 'Personenakte · Offboarding',
    icon: 'logout',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'hr-contracts-bulk',
    path: '/hr/contracts/bulk',
    label: 'Verträge · Sammelaktionen',
    icon: 'library_add',
    component: NoRoutePlaceholder,
    noRoute: true,
    noPage: true,
  },
  {
    id: 'schulen',
    path: '/schulen',
    label: 'Schulen',
    icon: 'school',
    component: SchulenDashboard,
  },
  {
    id: 'schulen-hierarchie',
    path: '/schulen/hierarchie',
    label: 'Hierarchie',
    icon: 'account_tree',
    component: SchulenHierarchie,
  },
  {
    id: 'schulen-kuechen',
    path: '/schulen/kuechen',
    label: 'Geteilte Küchen',
    icon: 'restaurant',
    component: KuechenPage,
  },
  {
    id: 'schulen-sperrtag-kontrolle',
    path: '/schulen/sperrtag-kontrolle',
    label: 'Kalender-Kontrolle',
    icon: 'calendar_month',
    component: SperrtagKontrollePage,
  },
  {
    id: 'schulen-karten',
    path: '/schulen/karten',
    label: 'Karten',
    icon: 'badge',
    component: KartenPage,
  },
  {
    id: 'schulen-lieferstellen',
    path: '/schulen/lieferstellen',
    label: 'Lieferstellen',
    icon: 'pin_drop',
    component: LieferstellenPage,
  },

  // ── Betrieb (Operations) ──────────────────────────────────────────────────
  {
    id: 'betrieb',
    path: '/betrieb',
    label: 'Command Center',
    icon: 'space_dashboard',
    component: CommandCenter,
  },
  {
    id: 'betrieb-projekte',
    path: '/betrieb/projekte',
    label: 'Projekte',
    icon: 'folder_supervised',
    component: ProjektePage,
  },
  {
    id: 'betrieb-standorte',
    path: '/betrieb/standorte',
    label: 'Standorte',
    icon: 'location_on',
    component: StandortePage,
  },
  {
    id: 'betrieb-organigramm',
    path: '/betrieb/organigramm',
    label: 'Organigramm',
    icon: 'hub',
    component: OrganigrammPage,
  },
  {
    id: 'betrieb-planung',
    path: '/betrieb/planung',
    label: 'Betriebsplanung',
    icon: 'event_note',
    component: BetriebsplanungPage,
  },
  {
    id: 'betrieb-touren',
    path: '/betrieb/touren',
    label: 'Touren',
    icon: 'local_shipping',
    component: TourenPage,
  },

  {
    id: 'support',
    path: '/support',
    label: 'Support',
    icon: 'support_agent',
    component: SupportDashboard,
  },
  {
    id: 'support-megatabelle-pca',
    path: '/support/megatabelle/pca',
    label: 'PCA · Schülerübersicht',
    icon: 'child_care',
    component: MegaTabellePca,
  },
  {
    id: 'support-megatabelle-v2-datenpull',
    path: '/support/megatabelle/v2-mockup/datenpull',
    label: 'MegaTabelle v2 · Datenpull',
    icon: 'cloud_download',
    component: MegaTabelleV2Datenpull,
  },
  {
    id: 'support-but-master',
    path: '/support/but-master',
    label: 'BuT Master',
    icon: 'fact_check',
    component: ButMasterPage,
  },
  {
    id: 'turbo-support',
    path: '/support/turbo',
    label: 'Turbo Support',
    icon: 'bolt',
    component: TurboSupportBuero,
  },
  {
    id: 'support-auswertung',
    path: '/support/auswertung',
    label: 'Support-Auswertung',
    icon: 'leaderboard',
    component: SupportAuswertung,
  },
  {
    id: 'support-schueler',
    path: '/support/schueler',
    label: 'Schüler & PCA',
    icon: 'child_care',
    component: SchuelerPCA,
  },
  {
    id: 'it',
    path: '/it',
    label: 'IT Dashboard',
    icon: 'computer',
    component: ITDashboard,
  },
  {
    id: 'it-compliance',
    path: '/it/compliance',
    label: 'Compliance & NIS2',
    icon: 'verified_user',
    component: ITCompliance,
  },
  {
    id: 'it-tech-stack',
    path: '/it/compliance/tech-stack',
    label: 'Tech Stack',
    icon: 'lan',
    component: TechStackPage,
  },
  {
    id: 'it-fleetops',
    path: '/it/fleetops',
    label: 'FleetOps',
    icon: 'devices',
    component: FleetOps,
  },
  {
    id: 'it-helpdesk',
    path: '/it/helpdesk',
    label: 'IT Helpdesk',
    icon: 'computer',
    component: ItHelpdeskBuero,
  },
  {
    id: 'schulportal-module',
    path: '/it/schulportal-module',
    label: 'Schulportal-Module',
    icon: 'toggle_on',
    component: SchulportalModule,
  },

  {
    id: 'it-role-admin',
    path: '/it/role-admin',
    label: 'Rollen & Rechte',
    icon: 'admin_panel_settings',
    component: RoleAdmin,
  },

  // Design Templates
  {
    id: 'admin-firma-policy',
    path: '/admin/firma-policy',
    label: 'Firma-Policy',
    icon: 'policy',
    component: FirmaPolicyPage,
  },
  {
    id: 'admin-schuljahr-policy',
    path: '/admin/schuljahr-policy',
    label: 'Schuljahr & Ferien',
    icon: 'calendar_month',
    component: SchuljahrPolicyPage,
  },
  {
    id: 'admin-eventlog',
    path: '/admin/eventlog',
    label: 'Event Log',
    icon: 'history',
    component: EventLogPage,
  },

  { id: 'glass-1', path: '/design/glass-1', label: 'Glass Financial', icon: 'payments', component: GlassTemplate1 },
  { id: 'glass-2', path: '/design/glass-2', label: 'Glass Project', icon: 'account_tree', component: GlassTemplate2 },
  { id: 'motion-1', path: '/design/motion-1', label: 'Motion Cockpit', icon: 'terminal', component: MotionTemplate1 },
  { id: 'motion-2', path: '/design/motion-2', label: 'Motion Assets', icon: 'precision_manufacturing', component: MotionTemplate2 },
  { id: 'component-gallery', path: '/design/components', label: 'UI Gallery', icon: 'widgets', component: ComponentGallery },
  { id: 'liquid-template', path: '/design/liquid', label: 'Liquid Architecture', icon: 'auto_awesome', component: LiquidTemplate },

  // ── Verwaltung — Lieferanten ──────────────────────────────────────────────
  {
    id: 'verwaltung-lieferanten',
    path: '/verwaltung/lieferanten',
    label: 'Lieferanten',
    icon: 'storefront',
    component: LieferantenConsole,
  },

  // ── Verwaltung — Kostenstellen ────────────────────────────────────────────
  // All entries below carry noRoute:true — actual routing happens in
  // router.tsx via a nested group with the KostenstellenPage SubNav layout.
  // Listed here so they appear in /it/role-admin Module-tab + permission UIs.
  {
    id: 'verwaltung-kostenstellen',
    path: '/verwaltung/kostenstellen',
    label: 'Kostenstellen',
    icon: 'account_balance',
    component: KostenstellenLayout,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-uebersicht',
    path: '/verwaltung/kostenstellen/uebersicht',
    label: 'KS — Übersicht',
    icon: 'map',
    component: KostenstellenUebersicht,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports',
    path: '/verwaltung/kostenstellen/imports',
    label: 'KS — Imports',
    icon: 'cloud_upload',
    component: KostenstellenImports,
    noRoute: true,
  },
  {
    // Backend-only: DATEV payroll import (functions/datevPayroll*.js), revocable
    // separately from the other imports. Page lives under imports/datev.
    id: 'verwaltung-kostenstellen-imports-datev',
    path: '/verwaltung/kostenstellen/imports/datev',
    label: 'KS — DATEV-Import',
    icon: 'request_quote',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  // Kostenstellen sub-pages: each has its own module so /it/role-admin can
  // switch it separately. KostenstellenPage guards the child route with the
  // LONGEST matching registry path, so an entry here is all it takes.
  // Seeded from the parent module's grants by mig 335 (no access change).
  {
    id: 'verwaltung-kostenstellen-imports-ass',
    path: '/verwaltung/kostenstellen/imports/ass',
    label: 'KS — Import ASS',
    icon: 'receipt_long',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-cc-push',
    path: '/verwaltung/kostenstellen/imports/chefsculinar/push',
    label: 'KS — ChefsCulinar Push-Protokoll',
    icon: 'sync_alt',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-rosenkrantz-konten',
    path: '/verwaltung/kostenstellen/imports/rosenkrantz/konten',
    label: 'KS — Rosenkrantz Konten',
    icon: 'account_tree',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-rosenkrantz-abruf',
    path: '/verwaltung/kostenstellen/imports/rosenkrantz/abruf',
    label: 'KS — Rosenkrantz Abruf',
    icon: 'cloud_download',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-pruefung',
    path: '/verwaltung/kostenstellen/imports/pruefung',
    label: 'KS — Routing-Prüfung',
    icon: 'rule',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-katalog',
    path: '/verwaltung/kostenstellen/imports/katalog',
    label: 'KS — Katalog-Import',
    icon: 'menu_book',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-refood',
    path: '/verwaltung/kostenstellen/imports/refood',
    label: 'KS — Re-Food-Import',
    icon: 'recycling',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-winfood',
    path: '/verwaltung/kostenstellen/imports/winfood',
    label: 'KS — Winfood-Import',
    icon: 'request_page',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-bruening-avis',
    path: '/verwaltung/kostenstellen/imports/bruening/avis',
    label: 'KS — Brüning Zahlungsavis',
    icon: 'payments',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-nette',
    path: '/verwaltung/kostenstellen/imports/nette',
    label: 'KS — Nette-Papier-Import',
    icon: 'description',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-imports-sap-umsaetze',
    path: '/verwaltung/kostenstellen/imports/sap-umsaetze',
    label: 'KS — SAP-Umsätze',
    icon: 'point_of_sale',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-stammdaten',
    path: '/verwaltung/kostenstellen/stammdaten',
    label: 'KS — Stammdaten',
    icon: 'account_tree',
    component: KostenstellenStammdaten,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-refood',
    path: '/verwaltung/kostenstellen/refood',
    label: 'KS — Re-Food',
    icon: 'recycling',
    component: KostenstellenRefood,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-auswertung',
    path: '/verwaltung/kostenstellen/auswertung',
    label: 'KS — Auswertung',
    icon: 'analytics',
    component: KostenstellenAuswertung,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-auswertung-schule',
    path: '/verwaltung/kostenstellen/auswertung/schule/:schoolId',
    label: 'KS — Auswertung je Schule',
    icon: 'school',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-lieferanten',
    path: '/verwaltung/kostenstellen/lieferanten',
    label: 'KS — Lieferanten-Auswertung',
    icon: 'bar_chart',
    component: KostenstellenLieferanten,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-preiskatalog',
    path: '/verwaltung/kostenstellen/preiskatalog',
    label: 'KS — Preiskatalog',
    icon: 'menu_book',
    component: KostenstellenPreiskatalog,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-bio-quote',
    path: '/verwaltung/kostenstellen/bio-quote',
    label: 'KS — Bio-Quote',
    icon: 'eco',
    component: KostenstellenBioQuote,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-pca',
    path: '/verwaltung/kostenstellen/pca',
    label: 'KS — PCA Explorer',
    icon: 'query_stats',
    component: KostenstellenPcaExplorer,
    noRoute: true,
  },
  {
    id: 'verwaltung-kostenstellen-sap-kasse',
    path: '/verwaltung/kostenstellen/sap-kasse',
    label: 'KS — SAP Kasse',
    icon: 'storefront',
    component: KostenstellenSapKasse,
    noRoute: true,
  },

  // ── Verwaltung — Lieferanten erweitert ────────────────────────────────────
  {
    id: 'verwaltung-lieferanten-kategorisierung',
    path: '/verwaltung/lieferanten/kategorisierung',
    label: 'Kategorisierung',
    icon: 'category',
    component: LieferantenKategorisierung,
    noRoute: true,
  },
  {
    id: 'verwaltung-lieferanten-workbench',
    path: '/verwaltung/lieferanten/workbench',
    label: 'Mapping Workbench',
    icon: 'linked_services',
    component: LieferantenWorkbench,
    noRoute: true,
  },

  // ── Verwaltung — Kostenarten ──────────────────────────────────────────────
  {
    id: 'verwaltung-kostenarten',
    path: '/verwaltung/kostenarten',
    label: 'Kostenarten',
    icon: 'category',
    component: KostenartenPage,
  },

  // ── Verwaltung — Mitarbeiterportal (M26 admin, Task 14) ────────────────────
  // No noRoute — each section auto-mounts via the registry loop in router.tsx
  // (same pattern as 'schulen-sperrtag-kontrolle'). /verwaltung/mitarbeiterportal
  // itself redirects to …/schulungen (see router.tsx).
  {
    id: 'mitarbeiterportal-einstellungen',
    path: '/verwaltung/mitarbeiterportal/einstellungen',
    label: 'Einstellungen',
    icon: 'settings',
    component: MitarbeiterportalEinstellungenPage,
  },
  {
    id: 'mitarbeiterportal-schulungen',
    path: '/verwaltung/mitarbeiterportal/schulungen',
    label: 'Schulungen',
    icon: 'school',
    component: MitarbeiterportalSchulungenPage,
  },
  {
    // Nur eine Berechtigung, keine Seite (Owner 01.10.2026): „write" erlaubt,
    // Schulungen MIT abgeschlossenen Versuchen zu löschen. ':id' hält den
    // Eintrag aus der Befehlspalette.
    id: 'mitarbeiterportal-schulungen-loeschen',
    path: '/verwaltung/mitarbeiterportal/schulungen/:id/loeschen',
    label: 'Schulungen löschen',
    icon: 'delete',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    // Same module id as Schulungen on purpose (§4.3): the dashboard is a view of
    // that data, not a new permission to grant and forget.
    // Own module so the route is switchable separately (seeded from
    // 'mitarbeiterportal-schulungen' by mig 335; its API still checks that one).
    id: 'mitarbeiterportal-dashboard',
    path: '/verwaltung/mitarbeiterportal/dashboard',
    label: 'Schulungs-Dashboard',
    icon: 'monitoring',
    component: MitarbeiterportalDashboardPage,
  },
  {
    id: 'mitarbeiterportal-dokumente',
    path: '/verwaltung/mitarbeiterportal/dokumente',
    label: 'Dokumente',
    icon: 'description',
    component: MitarbeiterportalDokumentePage,
  },
  {
    id: 'mitarbeiterportal-mitteilungen',
    path: '/verwaltung/mitarbeiterportal/mitteilungen',
    label: 'Mitteilungen',
    icon: 'campaign',
    component: MitarbeiterportalMitteilungenPage,
  },

  // ── Kasse — Kontrolle (FM Kassenbuch ↔ SAP Bar reconciliation; IT-only) ────
  {
    id: 'kasse-kontrolle',
    path: '/kasse/kontrolle',
    label: 'Kasse — Kontrolle',
    icon: 'balance',
    component: KasseKontrolle,
  },

  // ── Verwaltung — HACCP (cold-chain temperature monitoring) ─────────────────
  {
    id: 'haccp',
    path: '/verwaltung/haccp',
    label: 'HACCP',
    icon: 'device_thermostat',
    component: HaccpLeitstandPage,
  },
  {
    // Dynamic :pointId segment — mounted manually in router.tsx (same pattern as
    // the other noRoute:true dynamic-param entries, e.g. hr-onboarding-probe-review).
    id: 'haccp-geraet',
    path: '/verwaltung/haccp/punkt/:pointId',
    label: 'HACCP — Gerät',
    icon: 'device_thermostat',
    component: HaccpGeraetPage,
    noRoute: true,
  },
  {
    id: 'haccp-berichte',
    path: '/verwaltung/haccp/berichte',
    label: 'HACCP — Berichte',
    icon: 'description',
    component: HaccpBerichtePage,
    noRoute: true,
  },
  {
    id: 'haccp-einstellungen',
    path: '/verwaltung/haccp/einstellungen',
    label: 'HACCP — Einstellungen',
    icon: 'settings',
    component: HaccpEinstellungenPage,
    noRoute: true,
  },
  {
    id: 'haccp-onboarding',
    path: '/verwaltung/haccp/onboarding',
    label: 'HACCP — Gerät hinzufügen',
    icon: 'add_circle',
    component: HaccpOnboardingPage,
    noRoute: true,
  },

  // ── Qualität (M30 QM) ───────────────────────────────────────────────────────
  {
    // The field app lives in its own entry (qm.html). /qm is mounted by hand in router.tsx (QmWeiche).
    id: 'qm',
    path: '/qm',
    label: 'Qualität — Kontrolle (Feld-App)',
    icon: 'fact_check',
    component: NoRoutePlaceholder,
    noRoute: true,
  },
  {
    id: 'qm-leitung',
    path: '/qm-leitung',
    label: 'Qualität — Leitung',
    icon: 'verified',
    component: NoRoutePlaceholder,
    noRoute: true,
  },

  // ── PCA — portal analytics (top-level section) ────────────────────────────
  {
    id: 'pca-anmeldezahlen',
    path: '/pca/anmeldezahlen',
    label: 'Anmeldezahlen',
    icon: 'bar_chart_4_bars',
    component: PcaAnmeldezahlenPage,
  },
  {
    id: 'speiseplan',
    path: '/speiseplan',
    label: 'Wochenplanung',
    icon: 'restaurant_menu',
    component: SpeiseplanPage,
  },
  {
    id: 'speiseplan-gerichte',
    path: '/speiseplan/gerichte',
    label: 'Gerichte',
    icon: 'menu_book',
    component: GerichtePage,
  },
  {
    id: 'pca-anmeldekontrolle',
    path: '/gigamail/anmeldekontrolle',
    label: 'Anmeldekontrolle',
    icon: 'shield',
    component: AnmeldekontrollePage,
  },

  // ── Verwaltung — Einstellungen / Performance ──────────────────────────────
  {
    id: 'verwaltung-einstellungen-kpis',
    path: '/verwaltung/einstellungen/kpis',
    label: 'KPI-Einstellungen',
    icon: 'tune',
    component: VerwaltungKpiSettings,
    noRoute: true,
  },

  // Bestellkorrektur — single-page Anmeldungsdatei-Korrektur tool
  {
    id: 'bestellkorrektur',
    path: '/bestellkorrektur',
    label: 'Bestellkorrektur',
    icon: 'manage_accounts',
    component: BestellkorrekturPage,
  },

  // Settings — available to every authenticated user; accessed from the
  // Header user-menu. Not rendered in the Sidebar (the Sidebar is hand-picked).
  { id: 'settings', path: '/settings', label: 'Einstellungen', icon: 'settings', component: SettingsPage },

  // Daten-Bot (M25 P2) — floating widget only, see NoRoutePlaceholder above.
  {
    id: 'bot',
    path: '/bot',
    label: 'Daten-Bot',
    icon: 'smart_toy',
    component: NoRoutePlaceholder,
    noRoute: true,
    noPage: true,
  },
]

export { NotFoundPage, AccessDeniedPage }
